import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const STAGED_MESSAGES = [
  'Validating delivery settings',
  'Creating the approval PDF',
  'Saving the secure approval record',
  'Submitting the email to Emailit',
  'Sending the customer SMS',
  'Finalising the approval request',
];

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const isValidAustralianMobile = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return /^04\d{8}$/.test(digits) || /^614\d{8}$/.test(digits);
};

function ReadinessRow({ ready, label, detail }) {
  return (
    <div className="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${ready ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
        <SafeIcon icon={ready ? FiIcons.FiCheck : FiIcons.FiAlertCircle} className="text-xs" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[10px] font-black text-gray-700 uppercase tracking-widest">{label}</span>
          <span className={`text-[9px] font-black uppercase tracking-wider ${ready ? 'text-emerald-600' : 'text-amber-600'}`}>
            {ready ? 'Ready' : 'Check'}
          </span>
        </div>
        <p className="text-[10px] text-gray-400 font-medium mt-0.5 break-words">{detail}</p>
      </div>
    </div>
  );
}

export default function SendForApprovalModal({
  open,
  proposal,
  loading,
  error,
  onClose,
  onSubmit,
}) {
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientMobile, setRecipientMobile] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [touched, setTouched] = useState({ name: false, email: false, mobile: false });
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [stageIndex, setStageIndex] = useState(0);
  const stageTimerRef = useRef(null);

  useEffect(() => {
    if (open && proposal) {
      setRecipientName(proposal.sections?.pointOfContact || proposal.clientName || '');
      setRecipientEmail(proposal.emailAddress || '');
      setRecipientMobile(
        proposal.clientMobile ||
          proposal.mobileNumber ||
          proposal.phoneNumber ||
          proposal.clientPhone ||
          ''
      );
      setCustomMessage('');
      setTouched({ name: false, email: false, mobile: false });
      setSubmitAttempted(false);
      setStageIndex(0);
    }
  }, [open, proposal]);

  useEffect(() => {
    if (loading) {
      setStageIndex(0);
      let idx = 0;
      stageTimerRef.current = setInterval(() => {
        idx += 1;
        if (idx < STAGED_MESSAGES.length) {
          setStageIndex(idx);
        }
      }, 3500);
    } else {
      if (stageTimerRef.current) {
        clearInterval(stageTimerRef.current);
        stageTimerRef.current = null;
      }
    }

    return () => {
      if (stageTimerRef.current) {
        clearInterval(stageTimerRef.current);
        stageTimerRef.current = null;
      }
    };
  }, [loading]);

  const nameReady = recipientName.trim().length > 0;
  const emailReady = isValidEmail(recipientEmail);
  const mobileReady = isValidAustralianMobile(recipientMobile);
  const canSubmit = nameReady && emailReady && mobileReady;
  const showNameError = (touched.name || submitAttempted) && !nameReady;
  const showEmailError = (touched.email || submitAttempted) && !emailReady;
  const showMobileError = (touched.mobile || submitAttempted) && !mobileReady;

  const handleSubmit = () => {
    setSubmitAttempted(true);
    if (!canSubmit) return;

    onSubmit({
      recipientName: recipientName.trim(),
      recipientEmail: recipientEmail.trim(),
      recipientMobile: recipientMobile.trim(),
      customMessage: customMessage.trim(),
    });
  };

  const proposalTitle = proposal?.title || 'Statement of Work';
  const clientName = proposal?.clientName || 'Client';

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[210] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
          />
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="review-send-title"
          >
            <div className="bg-slate-900 px-6 py-5 flex items-center gap-3 text-white">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/20 border border-indigo-400/20 flex items-center justify-center shrink-0">
                <SafeIcon icon={FiIcons.FiSend} className="text-indigo-300 text-lg" />
              </div>
              <div className="min-w-0">
                <p className="text-[9px] font-black text-indigo-300 uppercase tracking-[0.2em] mb-1">Client Approval</p>
                <h2 id="review-send-title" className="text-base font-black tracking-tight">Review &amp; Send</h2>
              </div>
            </div>

            <div className="p-6 space-y-5 overflow-y-auto max-h-[72vh]">
              <div className="rounded-2xl border border-gray-200 bg-gray-50/70 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Statement of Work</p>
                    <p className="text-sm font-black text-gray-900 leading-snug break-words">{proposalTitle}</p>
                    <p className="text-xs text-gray-500 font-medium mt-1 break-words">Prepared for {clientName}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <span className="px-2.5 py-1 rounded-md bg-indigo-50 text-indigo-700 text-[9px] font-black uppercase tracking-wider border border-indigo-100">
                      {proposal?.documentNumber || 'Number pending'}
                    </span>
                    {proposal?.version && (
                      <span className="px-2.5 py-1 rounded-md bg-white text-gray-600 text-[9px] font-bold uppercase tracking-wider border border-gray-200">
                        v{proposal.version}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <p className="text-[10px] font-black text-gray-900 uppercase tracking-widest">Send Readiness</p>
                    <p className="text-[10px] text-gray-400 font-medium mt-0.5">Confirm the person who will receive and verify this approval.</p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider border ${canSubmit ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                    {canSubmit ? 'Ready to Send' : 'Needs Attention'}
                  </span>
                </div>
                <div className="rounded-xl border border-gray-100 bg-white divide-y divide-gray-100 px-3.5 py-3 shadow-sm">
                  <ReadinessRow ready={nameReady} label="Approver" detail={nameReady ? recipientName.trim() : 'Enter the authorised client contact name.'} />
                  <ReadinessRow ready={emailReady} label="Email" detail={emailReady ? recipientEmail.trim() : 'Enter a valid approval email address.'} />
                  <ReadinessRow ready={mobileReady} label="Mobile Verification" detail={mobileReady ? recipientMobile.trim() : 'Enter an Australian mobile number for verification.'} />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                  Recipient Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, name: true }))}
                  disabled={loading}
                  aria-invalid={showNameError}
                  className={`w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium transition-colors ${showNameError ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`}
                  placeholder="Client contact name"
                />
                {showNameError && <p className="text-[10px] text-red-600 font-medium mt-1.5">Recipient name is required.</p>}
              </div>

              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                  Recipient Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
                  disabled={loading}
                  aria-invalid={showEmailError}
                  className={`w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium transition-colors ${showEmailError ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`}
                  placeholder="client@example.com"
                />
                {showEmailError && <p className="text-[10px] text-red-600 font-medium mt-1.5">Enter a valid email address.</p>}
              </div>

              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                  Approval Recipient Mobile <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={recipientMobile}
                  onChange={(e) => setRecipientMobile(e.target.value)}
                  onBlur={() => setTouched((prev) => ({ ...prev, mobile: true }))}
                  disabled={loading}
                  aria-invalid={showMobileError}
                  className={`w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium transition-colors ${showMobileError ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`}
                  placeholder="0425 228 778"
                />
                <p className={`text-[10px] mt-1.5 font-medium ${showMobileError ? 'text-red-600' : 'text-gray-400'}`}>
                  {showMobileError
                    ? 'Enter a valid Australian mobile number.'
                    : 'Accepted: 0425 228 778, 61 425 228 778 or +61 425 228 778.'}
                </p>
              </div>

              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                  Personal Message (Optional)
                </label>
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  disabled={loading}
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none text-sm resize-none"
                  placeholder="Add a brief note for the recipient…"
                />
              </div>

              <div className="flex items-start gap-3 bg-indigo-50/60 border border-indigo-100 rounded-xl p-4">
                <div className="w-8 h-8 rounded-lg bg-white border border-indigo-100 flex items-center justify-center shrink-0">
                  <SafeIcon icon={FiIcons.FiShield} className="text-indigo-500 text-sm" />
                </div>
                <div>
                  <p className="text-[10px] font-black text-indigo-800 uppercase tracking-widest mb-1">Secure approval package</p>
                  <p className="text-[11px] text-indigo-700 font-medium leading-relaxed">
                    A fresh professional PDF will be generated for this request. The recipient mobile is used by the existing verification flow before approval is confirmed.
                  </p>
                </div>
              </div>

              {loading && (
                <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <SafeIcon
                      icon={FiIcons.FiRefreshCw}
                      className="text-indigo-500 animate-spin shrink-0"
                    />
                    <p className="text-xs font-bold text-indigo-700">
                      {STAGED_MESSAGES[stageIndex]}
                    </p>
                  </div>
                  <div className="flex gap-1.5">
                    {STAGED_MESSAGES.map((msg, i) => (
                      <div
                        key={i}
                        className={`h-1.5 flex-1 rounded-full transition-all ${
                          i <= stageIndex ? 'bg-indigo-500' : 'bg-indigo-100'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-[10px] text-indigo-600/70 font-medium mt-3 italic text-center">
                    The first send creates and stores the exact approval PDF. Resends reuse that PDF and are normally faster.
                  </p>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-100 rounded-xl p-3.5 flex flex-col gap-2">
                  <div className="flex items-start gap-2.5">
                    <SafeIcon
                      icon={FiIcons.FiAlertTriangle}
                      className="text-red-500 text-sm mt-0.5 shrink-0"
                    />
                    <p className="text-xs text-red-700 font-bold break-words">{error}</p>
                  </div>
                  <p className="text-[10px] text-red-600 font-medium ml-6">
                    The approval record is preserved. Correct the delivery issue, then use Resend from the Client Approval card.
                  </p>
                </div>
              )}
            </div>

            <div className="bg-gray-50 px-6 py-4 flex items-center justify-between gap-3 border-t border-gray-100 shrink-0">
              <div className="hidden sm:flex items-center gap-2 text-[9px] font-bold uppercase tracking-widest text-gray-400">
                <SafeIcon icon={canSubmit ? FiIcons.FiCheckCircle : FiIcons.FiInfo} className={canSubmit ? 'text-emerald-500' : 'text-amber-500'} />
                {canSubmit ? 'Recipient details verified' : 'Complete required details'}
              </div>
              <div className="flex justify-end gap-3 ml-auto">
                <button
                  onClick={onClose}
                  disabled={loading}
                  className="px-5 py-2.5 text-xs font-black text-gray-500 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 uppercase tracking-widest transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !canSubmit}
                  className="flex items-center gap-2 px-6 py-2.5 text-xs font-black text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 uppercase tracking-widest shadow-lg shadow-indigo-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <SafeIcon
                    icon={loading ? FiIcons.FiRefreshCw : FiIcons.FiSend}
                    className={loading ? 'animate-spin' : ''}
                  />
                  {loading ? 'Sending…' : 'Send for Approval'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}